import { Tier } from "@/pages/Private/pages/OnboardingTour/components/steps/scoring/criteriaTiers";

/**
 * Flags AI-criterion buckets that the scoring engine cannot tell apart.
 *
 * An AI criterion is scored by asking the model which of the buckets applies,
 * using the free-text descriptions as-is. When two buckets state the *same
 * consequence* and differ only in their leading quantity, the model has nothing
 * to discriminate on but that quantity — and it gets it wrong. K2 Mobility's
 * "Fleet size (heavy vehicles)" criterion is the live example (PREC-3788): its
 * 0-point and 1-point descriptions are byte-identical after "<35 " / "35-100 ",
 * and companies with 50, 60 and 90 vehicles were scored 0 — a disqualification —
 * instead of the 1 point their band is worth.
 *
 * No prompt can reliably separate two options that say the same thing, so the
 * defect is surfaced where it is authored rather than worked around downstream.
 */

/** Separators an authored bucket uses between its quantity and its consequence. */
const QUANTITY_SEPARATOR = /\s[—–-]\s|:\s/;

/**
 * The part of a description that states the *consequence*, normalised. Buckets
 * are compared on this rather than the whole text, so "<35 heavy vehicles — lower
 * priority" and "35-100 heavy vehicles — lower priority" collide (they should)
 * while "…— strong opportunity" and "…— suitable for pilots" do not.
 */
export const bucketConsequence = (description: string): string => {
	const normalized = description.trim().toLowerCase().replace(/\s+/g, " ");
	const separatorMatch = normalized.match(QUANTITY_SEPARATOR);

	if (separatorMatch?.index === undefined) {
		return normalized;
	}

	return normalized.slice(separatorMatch.index + separatorMatch[0].length).trim();
};

/** One scored bucket of an AI criterion, as stored in the rubric JSON. */
interface BucketWithDescription {
	points?: number;
	description?: string;
}

/**
 * The points buckets whose description does not distinguish them from another
 * bucket of the same criterion. Buckets with no description yet are ignored —
 * an unfilled cell is a different problem.
 */
export const findNonDiscriminatingBuckets = (buckets: BucketWithDescription[] = []): Set<Tier> => {
	const bucketsByConsequence = new Map<string, Tier[]>();

	for (const bucket of buckets) {
		const description = bucket?.description?.trim();

		if (!description || bucket?.points === undefined) {
			continue;
		}

		const consequence = bucketConsequence(description);
		const points = bucket.points as Tier;

		bucketsByConsequence.set(consequence, [
			...(bucketsByConsequence.get(consequence) ?? []),
			points,
		]);
	}

	const flagged = new Set<Tier>();

	for (const sharing of bucketsByConsequence.values()) {
		if (sharing.length > 1) {
			sharing.forEach((points) => flagged.add(points));
		}
	}

	return flagged;
};
